// POC Certifications Service
// File: src/services/pocCertifications.service.ts

import { apiClient } from './api-client';
import { ApiResponse } from '../dtos/common/api.dto';
import {
  POCCertificationDocument,
  GeneratedFile,
  POCCertificationData,
  CreatePOCCertificationRequest,
  CreatePOCCertificationResponse,
  ListPOCCertificationsResponse,
  POCGetDocumentResponse,
  POCDocumentConfirmRequest,
  Cms485Version
} from '../dtos/clinical/poc-certification.dto';
import { PresignedUploadService, ticketRequestFor } from './uploads/presignedUpload';
import type { UploadTicket } from './uploads/presignedUpload';
import type {
  NursingAddendumDownloadData
} from '../dtos/patientmr/nursing-addendum.dto';

// Re-export types for backward compatibility
export type {
  POCCertificationDocument,
  GeneratedFile,
  POCCertificationData,
  CreatePOCCertificationRequest,
  CreatePOCCertificationResponse,
  ListPOCCertificationsResponse,
  Cms485Version
};

/** documentType of the physician-signed plan of care copy */
export const SIGNED_POC_DOCUMENT_TYPE = 'SIGNED_POC';

// Rename the POC GetDocumentResponse for backward compatibility
export type GetDocumentResponse = POCGetDocumentResponse;

// UI-facing discipline enum (kept in service for UI logic)
export enum POCDiscipline {
  RN = 'RN',
  PT = 'PT',
  OT = 'OT',
  ST = 'ST',
  MSW = 'MSW',
  HHA = 'HHA'
}

// API-facing discipline enum (kept in service for API mapping)
export enum ApiPOCDiscipline {
  RN = 'RN',
  PT = 'PT',
  OT = 'OT',
  ST = 'ST',
  MSW = 'MSW',
  HHA = 'HHA'
}

/**
 * Map UI discipline to API discipline
 * @param uiDiscipline - The UI-facing discipline value
 * @returns API-facing discipline value
 */
export const mapUiDisciplineToApiDiscipline = (uiDiscipline: string): string => {
  switch (uiDiscipline) {
    case POCDiscipline.RN:
      return ApiPOCDiscipline.RN;
    case POCDiscipline.PT:
      return ApiPOCDiscipline.PT;
    case POCDiscipline.OT:
      return ApiPOCDiscipline.OT;
    case POCDiscipline.ST:
      return ApiPOCDiscipline.ST;
    case POCDiscipline.MSW:
      return ApiPOCDiscipline.MSW;
    case POCDiscipline.HHA:
      return ApiPOCDiscipline.HHA;
    default:
      return uiDiscipline; // Return as-is if no mapping found
  }
};

/**
 * Map API discipline to UI discipline
 * @param apiDiscipline - The API-facing discipline value
 * @returns UI-facing discipline value
 */
const mapApiDisciplineToUiDiscipline = (apiDiscipline: string): string => {
  switch (apiDiscipline) {
    case ApiPOCDiscipline.RN:
      return POCDiscipline.RN;
    case ApiPOCDiscipline.PT:
      return POCDiscipline.PT;
    case ApiPOCDiscipline.OT:
      return POCDiscipline.OT;
    case ApiPOCDiscipline.ST:
      return POCDiscipline.ST;
    case ApiPOCDiscipline.MSW:
      return POCDiscipline.MSW;
    case ApiPOCDiscipline.HHA:
      return POCDiscipline.HHA;
    default:
      return apiDiscipline; // Return as-is if no mapping found
  }
};

/**
 * Map multiple disciplines from UI to API format
 * @param disciplines - Array of UI discipline values
 * @returns Array of API discipline values
 */
const mapDisciplinesToApi = (disciplines: string[]): string[] => {
  return disciplines.map(d => mapUiDisciplineToApiDiscipline(d));
};

/**
 * Map multiple disciplines from API to UI format
 * @param disciplines - Array of API discipline values
 * @returns Array of UI discipline values
 */
const mapDisciplinesFromApi = (disciplines: string[]): string[] => {
  return disciplines.map(d => mapApiDisciplineToUiDiscipline(d));
};

/**
 * Format POC certification data from API response
 * @param certification - POC certification data from API
 * @returns Formatted POC certification with UI-friendly disciplines
 */
const formatPOCCertification = (certification: any): POCCertificationData => {
  return {
    ...certification,
    discipline: certification.discipline
      ? mapApiDisciplineToUiDiscipline(certification.discipline)
      : certification.discipline,
    disciplines: certification.disciplines
      ? mapDisciplinesFromApi(certification.disciplines)
      : certification.disciplines
  };
};

/**
 * POC Certifications Service
 */
export const POCCertificationsService = {
  /**
   * Create a new POC certification
   */
  createPOCCertification: async (
    patientId: string,
    certificationData: CreatePOCCertificationRequest
  ): Promise<CreatePOCCertificationResponse> => {
    // Map disciplines from UI format to API format
    const apiData = {
      ...certificationData,
      discipline: mapUiDisciplineToApiDiscipline(certificationData.discipline),
      disciplines: certificationData.disciplines
        ? mapDisciplinesToApi(certificationData.disciplines)
        : undefined
    };

    const response: ApiResponse<CreatePOCCertificationResponse> = await apiClient.post(
      `/patientmr/${patientId}/poccertifications`,
      apiData
    );

    if (response.success && response.data) {
      // Map disciplines from API format to UI format in the response
      if (response.data.pocCertification) {
        response.data.pocCertification = formatPOCCertification(response.data.pocCertification);
      }
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to create POC certification');
  },

  /**
   * List all POC certifications for a patient
   */
  listPOCCertifications: async (patientId: string): Promise<ListPOCCertificationsResponse> => {
    const response: ApiResponse<ListPOCCertificationsResponse> = await apiClient.get(
      `/patientmr/${patientId}/poccertifications`
    );

    if (response.success && response.data) {
      // Map disciplines from API format to UI format in the response
      if (response.data.certifications) {
        response.data.certifications = response.data.certifications.map(formatPOCCertification);
      }
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to list POC certifications');
  },

  /**
   * Get a specific POC certification
   */
  getPOCCertification: async (
    patientId: string,
    certificationId: string
  ): Promise<POCCertificationData> => {
    const response: ApiResponse<POCCertificationData> = await apiClient.get(
      `/patientmr/${patientId}/poccertifications/${certificationId}`
    );

    if (response.success && response.data) {
      // Map disciplines from API format to UI format in the response
      return formatPOCCertification(response.data);
    }

    throw new Error(response.error?.message || 'Failed to get POC certification');
  },

  /**
   * Update an existing POC certification
   */
  updatePOCCertification: async (
    patientId: string,
    certificationId: string,
    certificationData: Partial<CreatePOCCertificationRequest>
  ): Promise<POCCertificationData> => {
    // Map disciplines from UI format to API format
    const apiData = {
      ...certificationData,
      discipline: certificationData.discipline
        ? mapUiDisciplineToApiDiscipline(certificationData.discipline)
        : undefined,
      disciplines: certificationData.disciplines
        ? mapDisciplinesToApi(certificationData.disciplines)
        : undefined
    };

    const response: ApiResponse<POCCertificationData> = await apiClient.put(
      `/patientmr/${patientId}/poccertifications/${certificationId}`,
      apiData
    );

    if (response.success && response.data) {
      // Map disciplines from API format to UI format in the response
      return formatPOCCertification(response.data);
    }

    throw new Error(response.error?.message || 'Failed to update POC certification');
  },

  /**
   * Delete a POC certification
   */
  deletePOCCertification: async (
    patientId: string,
    certificationId: string
  ): Promise<{ message: string }> => {
    const response: ApiResponse<{ message: string }> = await apiClient.delete(
      `/patientmr/${patientId}/poccertifications/${certificationId}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to delete POC certification');
  },

  /**
   * Generate CMS-485 form for a POC certification
   * @param patientId - Patient identifier
   * @param certificationId - POC Certification identifier
   * @param discipline - Discipline to generate form for (RN, PT, OT, ST, or ALL)
   * @param carePlanIds - Optional specific care plan IDs
   * @param assessmentIds - Optional specific assessment IDs
   */
  generateCMS485: async (
    patientId: string,
    certificationId: string,
    discipline: string = 'ALL',
    carePlanIds?: {
      nursingCarePlanId?: string;
      ptCarePlanId?: string;
      otCarePlanId?: string;
      stCarePlanId?: string;
    },
    assessmentIds?: {
      nursingAssessmentId?: string;
      ptAssessmentId?: string;
      otAssessmentId?: string;
      stAssessmentId?: string;
    }
  ): Promise<any> => {
    // Build request payload
    const payload: any = {
      discipline: discipline
    };

    // Add care plan IDs if provided
    if (carePlanIds) {
      Object.assign(payload, carePlanIds);
    }

    // Add assessment IDs if provided
    if (assessmentIds) {
      Object.assign(payload, assessmentIds);
    }

    const response: ApiResponse<any> = await apiClient.post(
      `/patientmr/${patientId}/poccertifications/${certificationId}/generateCMS485`,
      payload
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to generate CMS-485');
  },

  /**
   * Get a signed URL for downloading a document.
   * The API returns `{ document: { …, signedUrl } }`; generated files (CMS-485 versions)
   * need `fileType: 'generated'`, otherwise the API looks in the uploaded documents.
   */
  getDocumentSignedUrl: async (
    patientId: string,
    certificationId: string,
    documentId: string,
    options: { fileType?: 'uploaded' | 'generated' } = {}
  ): Promise<GetDocumentResponse> => {
    const query = options.fileType === 'generated' ? '?fileType=generated' : '';
    const response: ApiResponse<{ document?: GetDocumentResponse } & Partial<GetDocumentResponse>> = await apiClient.get(
      `/patientmr/${patientId}/poccertifications/${certificationId}/documents/${documentId}${query}`
    );

    const document = response.data?.document ?? (response.data?.signedUrl ? response.data as GetDocumentResponse : undefined);
    if (response.success && document) {
      return document;
    }

    throw new Error(response.error?.message || 'Failed to get document signed URL');
  },

  /**
   * Remove an uploaded document from a draft certification.
   * DELETE /patientmr/{patientId}/poccertifications/{id}/documents/{documentId}
   * The server answers 409 when the certification is no longer a draft or the
   * document is the signed copy; that message is thrown as-is.
   */
  deletePOCDocument: async (
    patientId: string,
    certificationId: string,
    documentId: string
  ): Promise<void> => {
    const response: ApiResponse<unknown> = await apiClient.delete(
      `/patientmr/${patientId}/poccertifications/${certificationId}/documents/${documentId}`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to remove the document');
    }
  },

  /**
   * Ask for a pre-signed upload ticket for a POC document (Phase 8).
   * POST /patientmr/{patientId}/poccertifications/{id}/documents/upload-url
   */
  getDocumentUploadTicket: async (
    patientId: string,
    certificationId: string,
    file: File
  ): Promise<UploadTicket> => {
    const response: ApiResponse<UploadTicket> = await apiClient.post(
      `/patientmr/${patientId}/poccertifications/${certificationId}/documents/upload-url`,
      ticketRequestFor(file)
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to get an upload link');
  },

  /**
   * Confirm an uploaded POC document (server checks it and adds it to the certification).
   * POST /patientmr/{patientId}/poccertifications/{id}/documents
   */
  confirmDocument: async (
    patientId: string,
    certificationId: string,
    body: POCDocumentConfirmRequest
  ): Promise<POCCertificationDocument> => {
    const response: ApiResponse<{ document?: POCCertificationDocument } & POCCertificationDocument> = await apiClient.post(
      `/patientmr/${patientId}/poccertifications/${certificationId}/documents`,
      body
    );
    if (response.success && response.data) {
      return response.data.document ?? response.data;
    }
    throw new Error(response.error?.message || 'Failed to save the document');
  },

  /**
   * Upload the physician-signed copy: ticket → S3 → confirm. Returns the stored document.
   */
  uploadSignedCopy: async (
    patientId: string,
    certificationId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<POCCertificationDocument> => {
    return PresignedUploadService.uploadAndConfirm(
      file,
      () => POCCertificationsService.getDocumentUploadTicket(patientId, certificationId, file),
      (key) => POCCertificationsService.confirmDocument(patientId, certificationId, {
        key,
        documentName: file.name,
        documentType: SIGNED_POC_DOCUMENT_TYPE,
      }),
      onProgress
    );
  },

  /** draft → pending_signature */
  sendForSignature: async (
    patientId: string,
    certificationId: string,
    details: { datePocSentToPhysician?: string; methodPocSent?: CreatePOCCertificationRequest['methodPocSent'] } = {}
  ): Promise<POCCertificationData> => {
    return POCCertificationsService.updatePOCCertification(patientId, certificationId, {
      status: 'pending_signature',
      ...details,
    });
  },

  /** pending_signature → signed; the signed copy must already be uploaded to this certification */
  markSigned: async (
    patientId: string,
    certificationId: string,
    signedDocumentId: string,
    details: { physicianSignatureDate?: string; dateSignedPotReceived?: string } = {}
  ): Promise<POCCertificationData> => {
    return POCCertificationsService.updatePOCCertification(patientId, certificationId, {
      status: 'signed',
      signedDocumentId,
      ...details,
    });
  },

  /** pending_signature → rejected (the physician declined; a new version is created as a new draft) */
  reject: async (
    patientId: string,
    certificationId: string
  ): Promise<POCCertificationData> => {
    return POCCertificationsService.updatePOCCertification(patientId, certificationId, {
      status: 'rejected',
    });
  },

  /**
   * Format date for display
   */
  formatDate: (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  },

  /**
   * Get status badge styles
   */
  getStatusBadgeStyles: (status: string): string => {
    switch (status) {
      case 'pending_signature':
        return 'bg-blue-100 text-blue-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  },

  /**
   * Maps backend-provided expirationAlertLevel to Tailwind badge CSS classes.
   * Backend computes the level; this function only maps level → visual style.
   * No date arithmetic here.
   */
  getExpirationBadgeStyles: (
    level?: 'green' | 'yellow' | 'orange' | 'red'
  ): string => {
    switch (level) {
      case 'red':    return 'bg-red-100 text-red-800';
      case 'orange': return 'bg-orange-100 text-orange-800';
      case 'yellow': return 'bg-yellow-100 text-yellow-800';
      case 'green':  return 'bg-green-100 text-green-800';
      default:       return 'bg-gray-100 text-gray-500';
    }
  },

  /**
   * Maps backend-provided fourteenDayRuleStatus to Tailwind badge CSS classes.
   * Backend computes the status; this function only maps status → visual style.
   * No date arithmetic here.
   */
  getFourteenDayBadgeStyles: (
    status?: 'not_applicable' | 'on_track' | 'compliant' | 'at_risk' | 'non_compliant'
  ): string => {
    switch (status) {
      case 'compliant':      return 'bg-green-100 text-green-800';
      case 'on_track':       return 'bg-green-50 text-green-700';
      case 'at_risk':        return 'bg-yellow-100 text-yellow-800';
      case 'non_compliant':  return 'bg-red-100 text-red-800';
      case 'not_applicable': return 'bg-gray-100 text-gray-500';
      default:               return 'bg-gray-100 text-gray-500';
    }
  },

  /**
   * Get a fresh 1-hour signed URL for an existing Nursing Addendum PDF.
   * GET /patientmr/{patientId}/poccertifications/{pocCertificationId}/nursing-addendum/{addendumId}/download
   */
  getNursingAddendumDownloadUrl: async (
    patientId: string,
    pocCertificationId: string,
    addendumId: string
  ): Promise<NursingAddendumDownloadData> => {
    const response: ApiResponse<NursingAddendumDownloadData> = await apiClient.get(
      `/patientmr/${patientId}/poccertifications/${pocCertificationId}/nursing-addendum/${addendumId}/download`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to get download URL');
  }
};

export default POCCertificationsService;
